import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Plus } from "lucide-react";
import { mutate } from "swr";
import {
  SkillFormDialog,
  SkillFormDialogMode,
  type SkillFormValues,
  type SkillSubmitResult,
} from "~/components/assistant/SkillFormDialog";
import SkillPickerModal from "~/components/assistant/SkillPickerModal";
import { SkillRow } from "~/components/assistant/SkillRow";
import type { LibrarySkill } from "~/components/assistant/hooks/useSkillLibrary";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import { useFetcher } from "~/hooks/useFetcher";
import { ConfirmDestructiveDialog } from "~/pages/manifold-nextgen/components/ConfirmDestructiveDialog";
import { TabHeader } from "~/pages/manifold-nextgen/components/TabHeader";
import { useCanEditAgent } from "~/pages/manifold-nextgen/hooks/useCanEditAgent";
import { Agent, AgentScope } from "~/services/agents";
import {
  PartialSkill,
  SKILL_LIST_LIMIT,
  SKILL_NAME_MAX_LENGTH,
  Skill,
  SkillPage,
  agentSkillsListPath,
  attachAgentSkillFromFields,
  detachAgentSkill,
  getSkill,
  updateSkill,
} from "~/services/skills";
import {
  extractErrorCode,
  getApiErrorMessage,
  isApiError,
  parseApiErrorBody,
} from "~/utils/apiClient";

type DialogContent =
  | { kind: "none" }
  | { kind: "picker" }
  | { kind: "create"; initialData?: Partial<SkillFormValues>; sourceName?: string }
  | { kind: "edit"; skill: Skill }
  | { kind: "view"; skill: Skill };

interface AgentSkillsTabProps {
  // Overrides the outlet-context agent — used when this tab is rendered
  // outside the AgentDetail route tree (the project context sidebar).
  agent?: Agent;
  // True when this tab is rendered inside the session context sidebar,
  // which only ever shows a read-only view (no edit affordances, no tab
  // title — the entity name is already shown in the sidebar header).
  sessionSidebarView?: boolean;
}

export const AgentSkillsTab = ({
  agent: agentProp,
  sessionSidebarView = false,
}: AgentSkillsTabProps) => {
  const outletAgent = useOutletContext<Agent>();
  const agent = agentProp ?? outletAgent;
  const canEditGrant = useCanEditAgent(agent);
  const canEdit = !sessionSidebarView && canEditGrant;
  const { showError, showSuccess } = useToast();

  const skillsKey = agentSkillsListPath(agent.id, { limit: SKILL_LIST_LIMIT });
  const { data, isLoading } = useFetcher<SkillPage>(skillsKey);
  const skills = data?.items ?? [];

  const [dialog, setDialog] = useState<DialogContent>({ kind: "none" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PartialSkill | null>(null);

  const refreshList = () => mutate(skillsKey);

  const closeDialog = () => setDialogOpen(false);

  const handleAdd = () => {
    setDialog({ kind: "picker" });
    setDialogOpen(true);
  };

  const handlePickFromLibrary = async (skill: LibrarySkill) => {
    try {
      const sourceSkill = await getSkill(skill.id);
      setDialog({
        kind: "create",
        sourceName: sourceSkill.name,
        initialData: {
          name: `${sourceSkill.name}-copy`.slice(0, SKILL_NAME_MAX_LENGTH),
          description: sourceSkill.description,
          content: sourceSkill.content,
        },
      });
    } catch {
      showError("Unable to load skill from the library");
    }
  };

  const handleCreateNew = () => setDialog({ kind: "create" });

  const handleView = async (skill: PartialSkill) => {
    try {
      const fullSkill = await getSkill(skill.id);
      setDialog({ kind: "view", skill: fullSkill });
      setDialogOpen(true);
    } catch {
      showError("Unable to load skill");
    }
  };

  const handleEdit = async (skill: PartialSkill) => {
    try {
      const fullSkill = await getSkill(skill.id);
      setDialog({ kind: "edit", skill: fullSkill });
      setDialogOpen(true);
    } catch {
      showError("Unable to load skill for editing");
    }
  };

  const handleEditorSubmit = async (values: SkillFormValues): Promise<SkillSubmitResult> => {
    try {
      if (dialog.kind === "edit") {
        await updateSkill(dialog.skill.id, values);
        showSuccess("Skill updated");
      } else if (dialog.kind === "create") {
        await attachAgentSkillFromFields(agent.id, values);
        showSuccess(dialog.sourceName ? "Skill copied to agent" : "New skill created on agent");
      } else {
        return { ok: false };
      }
      closeDialog();
      await refreshList();
      return { ok: true };
    } catch (e) {
      if (isApiError(e) && e.status === 409) {
        const code = extractErrorCode(parseApiErrorBody(e));
        // The agent-skills attach route's only 409 is a duplicate name, confirmed
        // via the standard envelope's `code`. The legacy skill-update route
        // returns a bare 409 with no envelope (code is null); it's still treated
        // as a name conflict since that's its only 409 case too.
        if (code === "conflict" || code === null) {
          const message = "A skill with this name already exists on this agent.";
          showError(`${message} Choose a different name.`);
          return { ok: false, field: "name", message };
        }
      }
      showError(isApiError(e) ? getApiErrorMessage(e) : "Unable to save skill");
      return { ok: false };
    }
  };

  const handleDeleteConfirm = async (skill: PartialSkill) => {
    try {
      await detachAgentSkill(agent.id, skill.id);
      showSuccess(`"${skill.name}" removed`);
      await refreshList();
    } catch (e) {
      showError(isApiError(e) ? getApiErrorMessage(e) : "Failed to remove skill");
      throw e;
    }
  };

  const skillFormInitialData =
    dialog.kind === "edit" || dialog.kind === "view"
      ? {
          name: dialog.skill.name,
          description: dialog.skill.description,
          content: dialog.skill.content,
        }
      : dialog.kind === "create"
        ? dialog.initialData
        : undefined;

  return (
    <>
      <div className="mx-auto flex max-w-3xl flex-col gap-4">
        <TabHeader
          title={sessionSidebarView ? undefined : "Skills"}
          showReadOnlyBadge={!sessionSidebarView && agent.scope === AgentScope.SYSTEM}
          context={
            isLoading
              ? undefined
              : `${skills.length} installed · Markdown instructions the agent reads on every turn`
          }
          description="Skills are domain-specific instructions and workflows the agent applies when relevant. Add ones from the library or write your own."
          action={
            canEdit && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleAdd}
                className="text-primary hover:text-primary/80"
              >
                <Plus />
                Add skill
              </Button>
            )
          }
        />

        {isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        ) : skills.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center">
            <p className="text-sm text-muted-foreground">No skills installed.</p>
          </div>
        ) : (
          <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
            {skills.map((skill) => (
              <SkillRow
                key={skill.id}
                skill={skill}
                canEdit={canEdit}
                onView={() => handleView(skill)}
                onEdit={() => handleEdit(skill)}
                onDelete={() => setDeleteTarget(skill)}
              />
            ))}
          </ul>
        )}
      </div>

      <SkillPickerModal
        open={dialogOpen && dialog.kind === "picker"}
        onClose={closeDialog}
        onPick={handlePickFromLibrary}
        onCreateNew={handleCreateNew}
      />

      <SkillFormDialog
        open={
          dialogOpen &&
          (dialog.kind === "create" || dialog.kind === "edit" || dialog.kind === "view")
        }
        onClose={closeDialog}
        onSubmit={handleEditorSubmit}
        mode={dialog.kind === "edit" ? SkillFormDialogMode.EDIT : SkillFormDialogMode.CREATE}
        readOnly={dialog.kind === "view"}
        initialData={skillFormInitialData}
        duplicateSourceName={dialog.kind === "create" ? dialog.sourceName : undefined}
        description={
          dialog.kind === "create"
            ? dialog.sourceName
              ? `Edit before saving — original is unchanged. This copy will be attached to ${agent.display_name}.`
              : `This skill will be attached to ${agent.display_name}.`
            : undefined
        }
      />

      <ConfirmDestructiveDialog
        target={deleteTarget}
        title="Remove this skill?"
        description={(skill) => (
          <>
            &ldquo;{skill.name}&rdquo; will be removed from {agent.display_name}. This does not
            affect the library source it was copied from.
          </>
        )}
        confirmLabel="Remove"
        pendingLabel="Removing…"
        onConfirm={handleDeleteConfirm}
        onClose={() => setDeleteTarget(null)}
      />
    </>
  );
};
